import { PLAElementRecord, PLARelationRecord, PLAProject, AttributeValue, PLAElementTypeDefinition, PLAValueType, isPLANumberValueType } from "./PLAState";

type TokenType = "number" | "string" | "identifier" | "attribute" | "operator" | "paren" | "comma" | "dot" | "comparison" | "question" | "colon";
type Token = { type: TokenType; value: string };
type StringExpressionNode = { type: "constant"; value: string } | { type: "attribute"; name: string } | { type: "concat"; left: StringExpressionNode; right: StringExpressionNode };
type AttributeReference = { type: "literal"; name: string } | { type: "localAttribute"; name: string } | { type: "stringExpression"; expression: StringExpressionNode };

type ExpressionNode =
   | { type: "number"; value: number }
   | { type: "string"; value: string }
   | { type: "attribute"; name: string }
   | { type: "binary"; operator: string; left: ExpressionNode; right: ExpressionNode }
   | { type: "conditional"; condition: ExpressionNode; whenTrue: ExpressionNode; whenFalse: ExpressionNode }
   | { type: "function"; name: string; args: ExpressionNode[] }
   | { type: "traversal"; aggregator: string; relationType: string; attribute: AttributeReference; deep?: boolean; functionName?: string }
   | { type: "parentLookup"; relationType: string; attribute: AttributeReference }
   | { type: "parentTraversal"; aggregator: string; parentRelationType: string; relationType: string; attribute: AttributeReference };

export type PLAEvaluationContext = {
   element: PLAElementRecord;
   elements: PLAElementRecord[];
   relations: PLARelationRecord[];
   project?: PLAProject;
   calculated?: Record<string, any>;
   resolveCalculatedAttribute?: (element: PLAElementRecord, attributeId: string) => any;
};

export type PLAExpressionValidationContext = {
   project?: PLAProject;
   elementTypeId?: string;
   elementType?: PLAElementTypeDefinition;
};

export type PLAExpressionExplanationStep = {
   label: string;
   value: any;
   valueType: string;
   details?: string[];
   children?: PLAExpressionExplanationStep[];
};

export type PLAExpressionExplanation = {
   expression: string;
   result: any;
   valueType: string;
   root: PLAExpressionExplanationStep;
   lines: string[];
};

const aggregateFunctions = ["sum", "avg", "min", "max", "count"];
const traversalFunctions = [...aggregateFunctions, "single"];
const deepTraversalFunctions: Record<string, string> = {
   deepSum: "sum",
   deepAvg: "avg",
   deepMin: "min",
   deepMax: "max",
   deepCount: "count",
   deepSingle: "single"
};
const parentTraversalFunctions: Record<string, string> = {
   parentSum: "sum",
   parentAvg: "avg",
   parentMin: "min",
   parentMax: "max",
   parentCount: "count",
   parentSingle: "single"
};
const parentLookupFunction = "parent";
const parsedExpressionCache = new Map<string, ExpressionNode>();
const parsedExpressionCacheMaxSize = 500;

type RelationIndexes = {
   outgoing: Map<string, PLARelationRecord[]>;
   incoming: Map<string, PLARelationRecord[]>;
   outgoingByType: Map<string, PLARelationRecord[]>;
   incomingByType: Map<string, PLARelationRecord[]>;
};

const elementIndexCache = new WeakMap<PLAElementRecord[], Map<string, PLAElementRecord>>();
const relationIndexCache = new WeakMap<PLARelationRecord[], RelationIndexes>();

function indexKey(elementId: string, relationType: string) {
   return `${elementId}\u0000${relationType}`;
}

function getElementsById(elements: PLAElementRecord[]): Map<string, PLAElementRecord> {
   const cached = elementIndexCache.get(elements);
   if (cached) return cached;
   const indexed = new Map(elements.map((element) => [element.id, element]));
   elementIndexCache.set(elements, indexed);
   return indexed;
}

function addIndexedRelation(map: Map<string, PLARelationRecord[]>, key: string, relation: PLARelationRecord) {
   const existing = map.get(key);
   if (existing) existing.push(relation);
   else map.set(key, [relation]);
}

function getRelationIndexes(relations: PLARelationRecord[]): RelationIndexes {
   const cached = relationIndexCache.get(relations);
   if (cached) return cached;
   const indexed: RelationIndexes = { outgoing: new Map(), incoming: new Map(), outgoingByType: new Map(), incomingByType: new Map() };
   relations.forEach((relation) => {
      addIndexedRelation(indexed.outgoing, relation.fromId, relation);
      addIndexedRelation(indexed.incoming, relation.toId, relation);
      addIndexedRelation(indexed.outgoingByType, indexKey(relation.fromId, relation.relationType), relation);
      addIndexedRelation(indexed.incomingByType, indexKey(relation.toId, relation.relationType), relation);
   });
   relationIndexCache.set(relations, indexed);
   return indexed;
}

function getRelatedRelations(context: PLAEvaluationContext, elementId: string, relationTypeId: string, direction: "outgoing" | "incoming"): PLARelationRecord[] {
   const indexes = getRelationIndexes(context.relations);
   if (relationTypeId === "outgoing") return indexes.outgoing.get(elementId) || [];
   if (relationTypeId === "incoming") return indexes.incoming.get(elementId) || [];
   return direction === "incoming" ? indexes.incomingByType.get(indexKey(elementId, relationTypeId)) || [] : indexes.outgoingByType.get(indexKey(elementId, relationTypeId)) || [];
}

function readEscapedText(expression: string, start: number, endChar: string): { value: string; end: number } {
   let value = "";
   let index = start;
   while (index < expression.length) {
      const char = expression[index];
      if (char === "\\") {
         if (index + 1 >= expression.length) throw new Error("Invalid escape sequence");
         value += expression[index + 1];
         index += 2;
         continue;
      }
      if (char === endChar) return { value, end: index };
      value += char;
      index++;
   }
   throw new Error(`Missing closing ${endChar}`);
}

function readQuotedIdentifier(expression: string, start: number): { value: string; end: number } {
   const quote = expression[start];
   const result = readEscapedText(expression, start + 1, quote);
   return { value: result.value, end: result.end + 1 };
}

function readBracketedAttributeReference(expression: string, start: number): { value: string; end: number } {
   let value = "";
   let depth = 0;
   let index = start;
   while (index < expression.length) {
      const char = expression[index];
      if (char === "\\") {
         if (index + 1 >= expression.length) throw new Error("Invalid escape sequence");
         value += expression[index + 1];
         index += 2;
         continue;
      }
      if (char === '"' || char === "'") {
         const quoted = readEscapedText(expression, index + 1, char);
         value += expression.slice(index, quoted.end + 1);
         index = quoted.end + 1;
         continue;
      }
      if (char === "[") {
         depth++;
         value += char;
         index++;
         continue;
      }
      if (char === "]") {
         if (depth === 0) return { value, end: index };
         depth--;
         value += char;
         index++;
         continue;
      }
      value += char;
      index++;
   }
   throw new Error("Missing closing ]");
}

function readIdentifier(expression: string, start: number): { value: string; end: number } | undefined {
   let value = "";
   let index = start;
   while (index < expression.length) {
      const char = expression[index];
      if (char === "\\") {
         if (index + 1 >= expression.length) throw new Error("Invalid escape sequence");
         value += expression[index + 1];
         index += 2;
         continue;
      }
      if (/[A-Za-z0-9_ÆØÅæøå-]/.test(char)) {
         value += char;
         index++;
         continue;
      }
      break;
   }
   if (!value || !/^[A-Za-z_ÆØÅæøå]/.test(value)) return undefined;
   return { value, end: index };
}

export function tokenizePLAExpression(expression: string): Token[] {
   const tokens: Token[] = [];
   let index = 0;

   while (index < expression.length) {
      const char = expression[index];
      if (/\s/.test(char)) {
         index++;
         continue;
      }
      if (char === "[") {
         const result = readBracketedAttributeReference(expression, index + 1);
         const name = result.value.trim();
         if (!name) throw new Error("Empty attribute reference");
         tokens.push({ type: "attribute", value: name });
         index = result.end + 1;
         continue;
      }
      if (char === '"' || char === "'") {
         const identifier = readQuotedIdentifier(expression, index);
         tokens.push({ type: "string", value: identifier.value });
         index = identifier.end;
         continue;
      }
      const comparison = expression.slice(index).match(/^(>=|<=|==|!=|>|<)/);
      if (comparison) {
         tokens.push({ type: "comparison", value: comparison[1] });
         index += comparison[1].length;
         continue;
      }
      if (/[+\-*/]/.test(char)) {
         tokens.push({ type: "operator", value: char });
         index++;
         continue;
      }
      if (char === "(" || char === ")") {
         tokens.push({ type: "paren", value: char });
         index++;
         continue;
      }
      if (char === ",") {
         tokens.push({ type: "comma", value: char });
         index++;
         continue;
      }
      if (char === "?") {
         tokens.push({ type: "question", value: char });
         index++;
         continue;
      }
      if (char === ":") {
         tokens.push({ type: "colon", value: char });
         index++;
         continue;
      }
      if (char === ".") {
         tokens.push({ type: "dot", value: char });
         index++;
         continue;
      }
      const numberMatch = expression.slice(index).match(/^\d+(?:\.\d+)?/);
      if (numberMatch) {
         tokens.push({ type: "number", value: numberMatch[0] });
         index += numberMatch[0].length;
         continue;
      }
      const identifier = readIdentifier(expression, index);
      if (identifier) {
         tokens.push({ type: "identifier", value: identifier.value });
         index = identifier.end;
         continue;
      }
      throw new Error(`Unexpected character '${char}'`);
   }

   return tokens;
}

class PLAExpressionParser {
   private position = 0;

   constructor(private readonly tokens: Token[]) { }

   parse(): ExpressionNode {
      const node = this.parseConditional();
      if (this.peek()) throw new Error(`Unexpected token '${this.peek()?.value}'`);
      return node;
   }

   private parseConditional(): ExpressionNode {
      const condition = this.parseComparison();
      if (this.peek()?.type !== "question") return condition;
      this.consume();
      const whenTrue = this.parseConditional();
      this.expect("colon", ":");
      const whenFalse = this.parseConditional();
      return { type: "conditional", condition, whenTrue, whenFalse };
   }

   private parseComparison(): ExpressionNode {
      let node = this.parseAdditive();
      while (this.peek()?.type === "comparison") {
         const operator = this.consume().value;
         node = { type: "binary", operator, left: node, right: this.parseAdditive() };
      }
      return node;
   }

   private parseAdditive(): ExpressionNode {
      let node = this.parseMultiplicative();
      while (this.peek()?.type === "operator" && ["+", "-"].includes(this.peek()!.value)) {
         const operator = this.consume().value;
         node = { type: "binary", operator, left: node, right: this.parseMultiplicative() };
      }
      return node;
   }

   private parseMultiplicative(): ExpressionNode {
      let node = this.parseUnary();
      while (this.peek()?.type === "operator" && ["*", "/"].includes(this.peek()!.value)) {
         const operator = this.consume().value;
         node = { type: "binary", operator, left: node, right: this.parseUnary() };
      }
      return node;
   }

   private parseUnary(): ExpressionNode {
      if (this.peek()?.type === "operator" && this.peek()?.value === "-") {
         this.consume();
         return { type: "binary", operator: "*", left: { type: "number", value: -1 }, right: this.parseUnary() };
      }
      return this.parsePrimary();
   }

   private parsePrimary(): ExpressionNode {
      const token = this.peek();
      if (!token) throw new Error("Unexpected end of expression");
      if (token.type === "number") {
         this.consume();
         return { type: "number", value: Number(token.value) };
      }
      if (token.type === "string") {
         this.consume();
         return { type: "string", value: token.value };
      }
      if (token.type === "attribute") {
         this.consume();
         return { type: "attribute", name: token.value };
      }
      if (token.type === "paren" && token.value === "(") {
         this.consume();
         const node = this.parseComparison();
         this.expect("paren", ")");
         return node;
      }
      if (token.type === "identifier") {
         return this.parseFunctionOrTraversal();
      }
      throw new Error(`Unexpected token '${token.value}'`);
   }

   private parseFunctionOrTraversal(): ExpressionNode {
      const functionName = this.expect("identifier").value;
      this.expect("paren", "(");
      if (parentTraversalFunctions[functionName] && this.isNameToken(this.peek()) && this.tokens[this.position + 1]?.type === "dot" && this.isNameToken(this.tokens[this.position + 2]) && this.tokens[this.position + 3]?.type === "dot") {
         const parentRelationType = this.expectName().value;
         this.expect("dot", ".");
         const relationType = this.expectName().value;
         this.expect("dot", ".");
         const attribute = this.parseAttributeReference();
         this.expect("paren", ")");
         return { type: "parentTraversal", aggregator: parentTraversalFunctions[functionName], parentRelationType, relationType, attribute };
      }
      if ((traversalFunctions.includes(functionName) || deepTraversalFunctions[functionName] || functionName === parentLookupFunction) && this.isNameToken(this.peek()) && this.tokens[this.position + 1]?.type === "dot") {
         const relationType = this.expectName().value;
         this.expect("dot", ".");
         const attribute = this.parseAttributeReference();
         this.expect("paren", ")");
         if (functionName === parentLookupFunction) return { type: "parentLookup", relationType, attribute };
         return { type: "traversal", aggregator: deepTraversalFunctions[functionName] || functionName, relationType, attribute, deep: Boolean(deepTraversalFunctions[functionName]), functionName };
      }
      const args: ExpressionNode[] = [];
      if (!(this.peek()?.type === "paren" && this.peek()?.value === ")")) {
         let hasMoreArgs = true;
         while (hasMoreArgs) {
            args.push(this.parseConditional());
            hasMoreArgs = this.peek()?.type === "comma";
            if (hasMoreArgs) this.consume();
         }
      }
      this.expect("paren", ")");
      return { type: "function", name: functionName, args };
   }

   private parseAttributeReference(): AttributeReference {
      const token = this.peek();
      if (token?.type === "identifier" || token?.type === "string") {
         const name = this.consume().value;
         if (!name.trim()) throw new Error("Empty quoted name");
         return { type: "literal", name };
      }
      if (token?.type === "attribute") return parseIndirectAttributeReference(this.consume().value);
      throw new Error("Expected attribute name");
   }

   private isNameToken(token: Token | undefined): boolean {
      return token?.type === "identifier" || token?.type === "string";
   }

   private expectName(): Token {
      const token = this.consume();
      if (!this.isNameToken(token)) throw new Error("Expected name");
      if (!token.value.trim()) throw new Error("Empty quoted name");
      return token;
   }

   private peek(): Token | undefined {
      return this.tokens[this.position];
   }

   private consume(): Token {
      return this.tokens[this.position++];
   }

   private expect(type: TokenType, value?: string): Token {
      const token = this.consume();
      if (!token || token.type !== type || (value !== undefined && token.value !== value)) {
         throw new Error(`Expected ${value ?? type}`);
      }
      return token;
   }
}

class PLAStringExpressionParser {
   private position = 0;

   constructor(private readonly expression: string) { }

   parse(): StringExpressionNode {
      const node = this.parseTerm();
      this.skipWhitespace();
      if (this.peek() !== "+") {
         if (this.position < this.expression.length) throw new Error(`Unexpected string expression token '${this.peek()}'`);
         return node;
      }
      this.consume();
      return { type: "concat", left: node, right: this.parse() };
   }

   private parseTerm(): StringExpressionNode {
      this.skipWhitespace();
      const char = this.peek();
      if (!char) throw new Error("Unexpected end of string expression");
      if (char === '"' || char === "'") {
         const quoted = readEscapedText(this.expression, this.position + 1, char);
         this.position = quoted.end + 1;
         return { type: "constant", value: quoted.value };
      }
      if (char === "[") {
         const reference = readEscapedText(this.expression, this.position + 1, "]");
         const name = reference.value.trim();
         if (!name) throw new Error("Empty attribute reference");
         this.position = reference.end + 1;
         return { type: "attribute", name };
      }

      const start = this.position;
      while (this.position < this.expression.length && this.expression[this.position] !== "+") this.position++;
      const name = this.expression.slice(start, this.position).trim();
      if (!name) throw new Error("Expected string expression term");
      return { type: "attribute", name };
   }

   private peek(): string | undefined {
      return this.expression[this.position];
   }

   private consume(): string {
      return this.expression[this.position++];
   }

   private skipWhitespace() {
      while (/\s/.test(this.peek() || "")) this.position++;
   }
}

function parseIndirectAttributeReference(expression: string): AttributeReference {
   if (!/[+"'\]]/.test(expression)) return { type: "localAttribute", name: expression };
   return { type: "stringExpression", expression: new PLAStringExpressionParser(expression).parse() };
}

export function parsePLAExpression(expression: string): ExpressionNode {
   const cached = parsedExpressionCache.get(expression);
   if (cached) return cached;
   const parsed = new PLAExpressionParser(tokenizePLAExpression(expression)).parse();
   const oldestExpression = parsedExpressionCache.keys().next().value;
   if (parsedExpressionCache.size >= parsedExpressionCacheMaxSize && oldestExpression !== undefined) parsedExpressionCache.delete(oldestExpression);
   parsedExpressionCache.set(expression, parsed);
   return parsed;
}

function findAttributeDefinitionId(reference: string, definitions: Record<string, { name: string }>): string | undefined {
   return Object.entries(definitions).find(([attributeId, definition]) => attributeId === reference || definition.name === reference)?.[0];
}

function findRelationTypeDefinitionId(reference: string, project: PLAProject): string | undefined {
   if (reference === "outgoing" || reference === "incoming") return reference;
   return Object.entries(project.relationTypes || {}).find(([relationTypeId, relationType]) => relationTypeId === reference || relationType.name === reference)?.[0];
}

function collectStringExpressionReferences(node: StringExpressionNode, attributes: string[]) {
   if (node.type === "attribute") attributes.push(node.name);
   if (node.type === "concat") {
      collectStringExpressionReferences(node.left, attributes);
      collectStringExpressionReferences(node.right, attributes);
   }
}

function collectAttributeReference(attribute: AttributeReference, references: { attributes: string[]; relationTypes: string[]; traversals: { relationType: string; attribute: AttributeReference }[] }) {
   if (attribute.type === "localAttribute") references.attributes.push(attribute.name);
   if (attribute.type === "stringExpression") collectStringExpressionReferences(attribute.expression, references.attributes);
}

function collectExpressionReferences(node: ExpressionNode, references: { attributes: string[]; relationTypes: string[]; traversals: { relationType: string; attribute: AttributeReference }[] }) {
   switch (node.type) {
      case "string":
         return;
      case "attribute":
         references.attributes.push(node.name);
         return;
      case "binary":
         collectExpressionReferences(node.left, references);
         collectExpressionReferences(node.right, references);
         return;
      case "conditional":
         collectExpressionReferences(node.condition, references);
         collectExpressionReferences(node.whenTrue, references);
         collectExpressionReferences(node.whenFalse, references);
         return;
      case "function":
         node.args.forEach((arg) => collectExpressionReferences(arg, references));
         return;
      case "traversal":
         collectAttributeReference(node.attribute, references);
         references.traversals.push({ relationType: node.relationType, attribute: node.attribute });
         return;
      case "parentLookup":
         collectAttributeReference(node.attribute, references);
         references.traversals.push({ relationType: node.relationType, attribute: node.attribute });
         return;
      case "parentTraversal":
         references.relationTypes.push(node.parentRelationType);
         collectAttributeReference(node.attribute, references);
         references.traversals.push({ relationType: node.relationType, attribute: node.attribute });
         return;
      default:
         return;
   }
}

function validateExpressionReferences(node: ExpressionNode, context: PLAExpressionValidationContext): string | undefined {
   const project = context.project;
   if (!project) return undefined;
   const references = { attributes: [] as string[], relationTypes: [] as string[], traversals: [] as { relationType: string; attribute: AttributeReference }[] };
   collectExpressionReferences(node, references);

   const elementType = context.elementType || (context.elementTypeId ? project.elementTypes?.[context.elementTypeId] : undefined);
   if (elementType) {
      for (const attributeName of references.attributes) {
         if (!findAttributeDefinitionId(attributeName, elementType.attributes || {})) return `Unknown attribute: ${attributeName}`;
      }
   }

   for (const relationType of references.relationTypes) {
      if (!findRelationTypeDefinitionId(relationType, project)) return `Unknown relation type: ${relationType}`;
   }

   for (const traversal of references.traversals) {
      if (!findRelationTypeDefinitionId(traversal.relationType, project)) return `Unknown relation type: ${traversal.relationType}`;
      if (traversal.attribute.type === "localAttribute") continue;
      const traversalAttributeName = traversal.attribute.type === "stringExpression" ? staticStringExpressionValue(traversal.attribute.expression) : traversal.attribute.name;
      if (traversalAttributeName === undefined) continue;
      const searchableElementTypes = [context.elementType, ...Object.values(project.elementTypes || {})].filter(Boolean) as PLAElementTypeDefinition[];
      const attributeExists = searchableElementTypes.some((type) => Boolean(findAttributeDefinitionId(traversalAttributeName, type.attributes || {})));
      if (!attributeExists) return `Unknown traversal attribute: ${traversalAttributeName}`;
   }
   return undefined;
}

function getAttributeDefinitionValueType(reference: string, elementType: PLAElementTypeDefinition | undefined): PLAValueType | undefined {
   if (!elementType) return undefined;
   const attributeId = findAttributeDefinitionId(reference, elementType.attributes || {});
   return attributeId ? elementType.attributes?.[attributeId]?.valueType : undefined;
}

function getTraversalAttributeValueType(reference: string, context: PLAExpressionValidationContext): PLAValueType | undefined {
   const project = context.project;
   if (!project) return undefined;
   const matchingTypes = [context.elementType, ...Object.values(project.elementTypes || {})].filter(Boolean) as PLAElementTypeDefinition[];
   const valueTypes = Array.from(new Set(matchingTypes.map((type) => getAttributeDefinitionValueType(reference, type)).filter(Boolean)));
   return valueTypes.length === 1 ? valueTypes[0] : undefined;
}

function staticAttributeReferenceValueType(attribute: AttributeReference, context: PLAExpressionValidationContext): PLAValueType | undefined {
   if (attribute.type === "localAttribute") return undefined;
   const attributeName = attribute.type === "stringExpression" ? staticStringExpressionValue(attribute.expression) : attribute.name;
   return attributeName === undefined ? undefined : getTraversalAttributeValueType(attributeName, context);
}

function inferExpressionValueType(node: ExpressionNode, context: PLAExpressionValidationContext): PLAValueType | undefined {
   const elementType = context.elementType || (context.elementTypeId ? context.project?.elementTypes?.[context.elementTypeId] : undefined);
   switch (node.type) {
      case "number": return "number";
      case "string": return "string";
      case "attribute": return getAttributeDefinitionValueType(node.name, elementType);
      case "binary": {
         const leftType = inferExpressionValueType(node.left, context);
         const rightType = inferExpressionValueType(node.right, context);
         if (["+", "-"].includes(node.operator) && leftType === rightType && isPLANumberValueType(leftType)) return leftType;
         if (["+", "-", "*", "/"].includes(node.operator)) return "number";
         return "boolean";
      }
      case "conditional": {
         const trueType = inferExpressionValueType(node.whenTrue, context);
         const falseType = inferExpressionValueType(node.whenFalse, context);
         return trueType === falseType ? trueType : undefined;
      }
      case "function": {
         if (node.name === "hasValue") return "boolean";
         if (node.name === "count") return "number";
         if (node.name === "firstValue" || node.name === "coalesce") {
            const valueTypes = Array.from(new Set(node.args.map((arg) => inferExpressionValueType(arg, context)).filter(Boolean)));
            return valueTypes.length === 1 ? valueTypes[0] : undefined;
         }
         const valueTypes = Array.from(new Set(node.args.map((arg) => inferExpressionValueType(arg, context)).filter((valueType) => isPLANumberValueType(valueType))));
         return valueTypes.length === 1 ? valueTypes[0] : "number";
      }
      case "traversal":
      case "parentLookup": return staticAttributeReferenceValueType(node.attribute, context);
      case "parentTraversal": return node.aggregator === "count" ? "number" : staticAttributeReferenceValueType(node.attribute, context);
      default: return undefined;
   }
}

function collectExpressionWarnings(node: ExpressionNode, context: PLAExpressionValidationContext, warnings: string[]) {
   if (node.type === "binary") {
      collectExpressionWarnings(node.left, context, warnings);
      collectExpressionWarnings(node.right, context, warnings);
      if (["+", "-"].includes(node.operator)) {
         const leftType = inferExpressionValueType(node.left, context);
         const rightType = inferExpressionValueType(node.right, context);
         if (isPLANumberValueType(leftType) && isPLANumberValueType(rightType) && leftType !== rightType && leftType !== "number" && rightType !== "number") {
            warnings.push(`Adding or subtracting different number types: ${leftType} ${node.operator} ${rightType}`);
         }
      }
      return;
   }
   if (node.type === "conditional") {
      collectExpressionWarnings(node.condition, context, warnings);
      collectExpressionWarnings(node.whenTrue, context, warnings);
      collectExpressionWarnings(node.whenFalse, context, warnings);
      return;
   }
   if (node.type === "function") node.args.forEach((arg) => collectExpressionWarnings(arg, context, warnings));
}

function staticStringExpressionValue(node: StringExpressionNode): string | undefined {
   if (node.type === "constant") return node.value;
   if (node.type === "attribute") return undefined;
   const left = staticStringExpressionValue(node.left);
   const right = staticStringExpressionValue(node.right);
   return left === undefined || right === undefined ? undefined : left + right;
}

export function validatePLAExpression(expression: string, context: PLAExpressionValidationContext = {}): { valid: boolean; error?: string; warnings?: string[] } {
   try {
      const node = parsePLAExpression(expression);
      const referenceError = validateExpressionReferences(node, context);
      if (referenceError) return { valid: false, error: referenceError };
      const warnings: string[] = [];
      collectExpressionWarnings(node, context, warnings);
      return warnings.length ? { valid: true, warnings } : { valid: true };
   } catch (error: any) {
      return { valid: false, error: error.message || String(error) };
   }
}

function numeric(value: any): number {
   if (value === true) return 1;
   if (value === false || value === undefined || value === null || value === "") return 0;
   const numberValue = Number(value);
   return Number.isFinite(numberValue) ? numberValue : 0;
}

function hasValidValue(value: any): boolean {
   if (value === undefined || value === null || value === "") return false;
   if (typeof value === "number" && !Number.isFinite(value)) return false;
   return true;
}

function resolveAttributeValue(attribute: AttributeValue | undefined, calculated: Record<string, any>, name: string): any {
   if (Object.prototype.hasOwnProperty.call(calculated, name)) return calculated[name];
   if (!attribute) return undefined;
   if (attribute.mode === "constant") return attribute.value;
   return undefined;
}

function findAttributeIdByReference(reference: string, context: PLAEvaluationContext, element: PLAElementRecord = context.element): string {
   if (Object.prototype.hasOwnProperty.call(element.attributes, reference)) return reference;
   const definitions = context.project?.elementTypes[element.elementType]?.attributes || {};
   return Object.entries(definitions).find(([attributeId, definition]) => attributeId === reference || definition.name === reference)?.[0] || reference;
}

function findRelationTypeIdByReference(reference: string, context: PLAEvaluationContext): string {
   if (reference === "outgoing" || reference === "incoming") return reference;
   const relationTypes = context.project?.relationTypes || {};
   return Object.entries(relationTypes).find(([relationTypeId, relationType]) => relationTypeId === reference || relationType.name === reference)?.[0] || reference;
}

function resolveAttributeReference(reference: string, context: PLAEvaluationContext): any {
   const attributeId = findAttributeIdByReference(reference, context);
   const definition = context.project?.elementTypes[context.element.elementType]?.attributes?.[attributeId];
   if (definition?.type === "expression") {
      if (Object.prototype.hasOwnProperty.call(context.calculated || {}, attributeId)) return context.calculated?.[attributeId];
      if (context.resolveCalculatedAttribute) return context.resolveCalculatedAttribute(context.element, attributeId);
      if (context.project) return resolvePLACalculatedAttributes(context.element, context.project, context.elements, context.relations)[attributeId];
   }
   return resolveAttributeValue(context.element.attributes[attributeId], context.calculated || {}, attributeId);
}

function compare(operator: string, left: any, right: any) {
   switch (operator) {
      case ">": return numeric(left) > numeric(right);
      case "<": return numeric(left) < numeric(right);
      case ">=": return numeric(left) >= numeric(right);
      case "<=": return numeric(left) <= numeric(right);
      case "==": return left == right;
      case "!=": return left != right;
      default: throw new Error(`Unsupported operator '${operator}'`);
   }
}

function valueType(value: any): string {
   if (value === null) return "null";
   if (value === undefined) return "undefined";
   if (Array.isArray(value)) return "array";
   return typeof value;
}

function displayReferenceValue(value: string, context: PLAEvaluationContext): string {
   const element = getElementsById(context.elements).get(value);
   if (element) return elementLabel(element);
   const elementType = context.project?.elementTypes?.[value];
   if (elementType?.name) return elementType.name;
   const relationType = context.project?.relationTypes?.[value];
   if (relationType?.name) return relationType.name;
   return value;
}

function escapeRegExp(value: string) {
   return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function sanitizeExplanationText(value: string, context: PLAEvaluationContext): string {
   const replacements = [
      ...context.elements.map((element) => [element.id, elementLabel(element)] as const),
      ...Object.entries(context.project?.elementTypes || {}).map(([elementTypeId, elementType]) => [elementTypeId, elementType.name] as const),
      ...Object.entries(context.project?.relationTypes || {}).map(([relationTypeId, relationType]) => [relationTypeId, relationType.name] as const)
   ].filter(([id, label]) => Boolean(id && label && id !== label)).sort((left, right) => right[0].length - left[0].length);
   return replacements.reduce((text, [id, label]) => text.replace(new RegExp(escapeRegExp(id), "g"), label), value);
}

function formatValue(value: any, context?: PLAEvaluationContext): string {
   if (typeof value === "string") return `"${context ? displayReferenceValue(value, context) : value}"`;
   if (value === undefined) return "undefined";
   return String(value);
}

function elementLabel(element: PLAElementRecord): string {
   return element.name || "Unnamed element";
}

function elementTypeLabel(elementTypeId: string, context: PLAEvaluationContext): string {
   return context.project?.elementTypes?.[elementTypeId]?.name || elementTypeId;
}

function relationTypeLabel(relationType: string, context: PLAEvaluationContext): string {
   if (relationType === "outgoing" || relationType === "incoming") return relationType;
   return context.project?.relationTypes?.[relationType]?.name || relationType;
}

function attributeLabel(reference: string, context: PLAEvaluationContext, element: PLAElementRecord = context.element): string {
   const attributeId = findAttributeIdByReference(reference, context, element);
   const definition = context.project?.elementTypes?.[element.elementType]?.attributes?.[attributeId];
   if (definition?.name) return definition.name;
   const matchingDefinition = Object.values(context.project?.elementTypes || {})
      .flatMap((elementType) => Object.entries(elementType.attributes || {}))
      .find(([attributeId, definition]) => attributeId === reference || definition.name === reference)?.[1];
   return matchingDefinition?.name || reference;
}

function attributeReferenceLabel(attribute: AttributeReference, context?: PLAEvaluationContext): string {
   if (attribute.type === "localAttribute") return `[${context ? attributeLabel(attribute.name, context) : attribute.name}]`;
   if (attribute.type === "stringExpression") return `[${stringExpressionLabel(attribute.expression, context)}]`;
   return context ? attributeLabel(attribute.name, context) : attribute.name;
}

function stringExpressionLabel(node: StringExpressionNode, context?: PLAEvaluationContext): string {
   switch (node.type) {
      case "constant": return `"${node.value}"`;
      case "attribute": return `[${context ? attributeLabel(node.name, context) : node.name}]`;
      case "concat": return `${stringExpressionLabel(node.left, context)} + ${stringExpressionLabel(node.right, context)}`;
      default: return "string expression";
   }
}

function nodeLabel(node: ExpressionNode, context?: PLAEvaluationContext): string {
   switch (node.type) {
      case "number": return String(node.value);
      case "string": return `"${context ? displayReferenceValue(node.value, context) : node.value}"`;
      case "attribute": return `[${context ? attributeLabel(node.name, context) : node.name}]`;
      case "binary": return `${nodeLabel(node.left, context)} ${node.operator} ${nodeLabel(node.right, context)}`;
      case "conditional": return `${nodeLabel(node.condition, context)} ? ${nodeLabel(node.whenTrue, context)} : ${nodeLabel(node.whenFalse, context)}`;
      case "function": return `${node.name}(${node.args.map((arg) => nodeLabel(arg, context)).join(", ")})`;
      case "traversal": return `${node.functionName || node.aggregator}(${context ? relationTypeLabel(node.relationType, context) : node.relationType}.${attributeReferenceLabel(node.attribute, context)})`;
      case "parentLookup": return `parent(${context ? relationTypeLabel(node.relationType, context) : node.relationType}.${attributeReferenceLabel(node.attribute, context)})`;
      case "parentTraversal": return `parent${node.aggregator}(${context ? relationTypeLabel(node.parentRelationType, context) : node.parentRelationType}.${context ? relationTypeLabel(node.relationType, context) : node.relationType}.${attributeReferenceLabel(node.attribute, context)})`;
      default: return "expression";
   }
}

function explanationStep(label: string, value: any, details?: string[], children?: PLAExpressionExplanationStep[]): PLAExpressionExplanationStep {
   return { label, value, valueType: valueType(value), details, children };
}

function formatExplanationLines(step: PLAExpressionExplanationStep, context: PLAEvaluationContext, depth = 0): string[] {
   const indent = "  ".repeat(depth);
   const lines = [`${indent}${sanitizeExplanationText(step.label, context)} = ${formatValue(step.value, context)} (${step.valueType})`];
   step.details?.forEach((detail) => lines.push(`${indent}  ${sanitizeExplanationText(detail, context)}`));
   step.children?.forEach((child) => lines.push(...formatExplanationLines(child, context, depth + 1)));
   return lines;
}

function explainAttributeReference(reference: string, context: PLAEvaluationContext): PLAExpressionExplanationStep {
   const attributeId = findAttributeIdByReference(reference, context);
   const definition = context.project?.elementTypes[context.element.elementType]?.attributes?.[attributeId];
   const value = resolveAttributeReference(reference, context);
   return explanationStep(`[${attributeLabel(reference, context)}]`, value, [
      `Element: ${elementLabel(context.element)}`,
      `Definition type: ${definition?.type || "unknown"}`,
      `Raw value type: ${valueType(value)}`
   ]);
}

function explainTraversalAttributeReference(attribute: AttributeReference, context: PLAEvaluationContext): { attributeName: string; step?: PLAExpressionExplanationStep } {
   if (attribute.type === "literal") return { attributeName: attribute.name };
   if (attribute.type === "stringExpression") {
      const step = explainStringExpression(attribute.expression, context);
      if (typeof step.value !== "string" || !step.value.trim()) throw new Error(`Cannot evaluate [${stringExpressionLabel(attribute.expression, context)}] as traversal attribute name: expected a non-empty string`);
      return { attributeName: step.value.trim(), step };
   }
   const step = explainAttributeReference(attribute.name, context);
   if (typeof step.value !== "string" || !step.value.trim()) throw new Error(`Cannot evaluate [${attributeLabel(attribute.name, context)}] as traversal attribute name: expected a non-empty string`);
   return { attributeName: step.value.trim(), step };
}

function explainStringExpression(node: StringExpressionNode, context: PLAEvaluationContext): PLAExpressionExplanationStep {
   switch (node.type) {
      case "constant": return explanationStep(stringExpressionLabel(node, context), node.value);
      case "attribute": return explainAttributeReference(node.name, context);
      case "concat": {
         const left = explainStringExpression(node.left, context);
         const right = explainStringExpression(node.right, context);
         const value = `${stringExpressionValue(left.value, stringExpressionLabel(node.left, context))}${stringExpressionValue(right.value, stringExpressionLabel(node.right, context))}`;
         return explanationStep(stringExpressionLabel(node, context), value, ["Operator: +"], [left, right]);
      }
      default: throw new Error("Unsupported string expression node");
   }
}

function explainAggregateValues(name: string, values: any[], label: string): any {
   return aggregateValues(name, values, label);
}

function explainTraversal(aggregator: string, relationType: string, attributeName: string, context: PLAEvaluationContext, deep = false, functionName = aggregator): PLAExpressionExplanationStep {
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const relationLabel = relationTypeLabel(relationTypeId, context);
   const attributeLabelText = attributeLabel(attributeName, context);
   const targets = new Set<string>();
   const pending = [context.element.id];
   const visited = new Set<string>();
   while (pending.length > 0) {
      const currentId = pending.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      getRelatedRelations(context, currentId, relationTypeId, relationTypeId === "incoming" ? "incoming" : "outgoing")
         .forEach((relation) => {
            const targetId = relationTypeId === "incoming" ? relation.fromId : relation.toId;
            if (targetId === context.element.id || targets.has(targetId)) return;
            if (deep) {
               targets.add(targetId);
               pending.push(targetId);
               return;
            }
            const targetElement = getElementsById(context.elements).get(targetId);
            if (!targetElement) return;
            const result = getElementAttributeValue(targetElement, attributeName, context);
            if (result.found) {
               targets.add(targetId);
            } else if (!visited.has(targetId)) {
               pending.push(targetId);
            }
         });
   }
   const valueSteps = Array.from(targets)
      .map((targetId) => context.elements.find((element) => element.id === targetId))
      .filter(Boolean)
      .map((element) => {
         const targetElement = element as PLAElementRecord;
         const value = getElementAttributeValue(targetElement, attributeName, context).value;
         return explanationStep(`${attributeLabelText} on ${elementLabel(targetElement)}`, value, [`Element type: ${elementTypeLabel(targetElement.elementType, context)}`]);
      });
   const values = valueSteps.map((step) => step.value);
   const result = explainAggregateValues(aggregator, values, `${functionName}(${relationLabel}.${attributeLabelText})`);
   return explanationStep(`${functionName}(${relationLabel}.${attributeLabelText})`, result, [
      `Start element: ${elementLabel(context.element)}`,
      `Traversal mode: ${deep ? "deep" : "stop at matching attribute"}`,
      `Visited elements: ${visited.size}`,
      `Related values: ${values.length}`
   ], valueSteps);
}

function explainParentLookup(relationType: string, attributeName: string, context: PLAEvaluationContext): PLAExpressionExplanationStep {
   if (!context.project) throw new Error("Cannot evaluate parent lookup without project definitions");
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const relationLabel = relationTypeLabel(relationTypeId, context);
   const attributeLabelText = attributeLabel(attributeName, context);
   const pending = [context.element.id];
   const visited = new Set<string>([context.element.id]);
   const inspected: PLAExpressionExplanationStep[] = [];

   while (pending.length > 0) {
      const currentId = pending.shift()!;
      const parentRelations = context.relations.filter((relation) => relation.toId === currentId && relation.relationType === relationTypeId);
      for (const relation of parentRelations) {
         if (visited.has(relation.fromId)) continue;
         visited.add(relation.fromId);
         const parentElement = context.elements.find((element) => element.id === relation.fromId);
         if (!parentElement) throw new Error(`Cannot evaluate parent(${relationLabel}.${attributeLabelText}): missing parent element`);
         const result = getElementAttributeValue(parentElement, attributeName, context);
         inspected.push(explanationStep(`Inspect parent ${elementLabel(parentElement)}`, result.value, [`Attribute found: ${result.found}`]));
         if (result.found) return explanationStep(`parent(${relationLabel}.${attributeLabelText})`, result.value, [], inspected);
         pending.push(parentElement.id);
      }
   }

   return explanationStep(`parent(${relationLabel}.${attributeLabelText})`, 0, [`No parent path with attribute ${attributeLabelText} found`], inspected);
}

function explainParentTraversal(aggregator: string, parentRelationType: string, relationType: string, attributeName: string, context: PLAEvaluationContext): PLAExpressionExplanationStep {
   if (!context.project) throw new Error("Cannot evaluate parent traversal without project definitions");
   const parentRelationTypeId = findRelationTypeIdByReference(parentRelationType, context);
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const parentRelationLabel = relationTypeLabel(parentRelationTypeId, context);
   const relationLabel = relationTypeLabel(relationTypeId, context);
   const attributeLabelText = attributeLabel(attributeName, context);
   const pending = [context.element.id];
   const visited = new Set<string>([context.element.id]);
   const inspected: PLAExpressionExplanationStep[] = [];

   while (pending.length > 0) {
      const currentId = pending.shift()!;
      const parentRelations = context.relations.filter((relation) => relation.toId === currentId && relation.relationType === parentRelationTypeId);
      for (const parentRelation of parentRelations) {
         if (visited.has(parentRelation.fromId)) continue;
         visited.add(parentRelation.fromId);
         const parentElement = context.elements.find((element) => element.id === parentRelation.fromId);
         if (!parentElement) throw new Error(`Cannot evaluate parent${aggregator}(${parentRelationLabel}.${relationLabel}.${attributeLabelText}): missing parent element`);

         const targetRelations = context.relations.filter((relation) => relation.fromId === parentElement.id && relation.relationType === relationTypeId);
         if (targetRelations.length > 0) {
            const valueSteps = targetRelations.map((relation) => {
               const targetElement = context.elements.find((element) => element.id === relation.toId);
               if (!targetElement) throw new Error(`Cannot evaluate parent${aggregator}(${parentRelationLabel}.${relationLabel}.${attributeLabelText}): missing related element`);
               const value = getElementAttributeValue(targetElement, attributeName, context).value;
               return explanationStep(`${attributeLabelText} on ${elementLabel(targetElement)}`, value);
            });
            const values = valueSteps.map((step) => step.value);
            const result = explainAggregateValues(aggregator, values, `parent${aggregator}(${parentRelationLabel}.${relationLabel}.${attributeLabelText})`);
            return explanationStep(`parent${aggregator}(${parentRelationLabel}.${relationLabel}.${attributeLabelText})`, result, [
               `Matched parent: ${elementLabel(parentElement)}`
            ], [...inspected, ...valueSteps]);
         }

         inspected.push(explanationStep(`Inspect parent ${elementLabel(parentElement)}`, "no related values"));
         pending.push(parentElement.id);
      }
   }

   return explanationStep(`parent${aggregator}(${parentRelationLabel}.${relationLabel}.${attributeLabelText})`, 0, [`No parent path with relation ${relationLabel} found`], inspected);
}

function explainNode(node: ExpressionNode, context: PLAEvaluationContext): PLAExpressionExplanationStep {
   switch (node.type) {
      case "number": return explanationStep(nodeLabel(node, context), node.value);
      case "string": return explanationStep(nodeLabel(node, context), node.value);
      case "attribute": return explainAttributeReference(node.name, context);
      case "binary": {
         const left = explainNode(node.left, context);
         const right = explainNode(node.right, context);
         let value: any;
         if ([">", "<", ">=", "<=", "==", "!="].includes(node.operator)) value = compare(node.operator, left.value, right.value);
         else if (node.operator === "+") value = numeric(left.value) + numeric(right.value);
         else if (node.operator === "-") value = numeric(left.value) - numeric(right.value);
         else if (node.operator === "*") value = numeric(left.value) * numeric(right.value);
         else if (node.operator === "/") value = numeric(left.value) / numeric(right.value);
         else throw new Error(`Unsupported operator '${node.operator}'`);
         return explanationStep(nodeLabel(node, context), value, [`Operator: ${node.operator}`], [left, right]);
      }
      case "conditional": {
         const condition = explainNode(node.condition, context);
         const selected = condition.value ? explainNode(node.whenTrue, context) : explainNode(node.whenFalse, context);
         return explanationStep(nodeLabel(node, context), selected.value, [`Condition result: ${formatValue(condition.value, context)}`], [condition, selected]);
      }
      case "function": {
         if (node.name === "hasValue") {
            if (node.args.length !== 1) throw new Error(`Cannot evaluate hasValue(...): expected exactly one argument but found ${node.args.length}`);
            try {
               const arg = explainNode(node.args[0], context);
               return explanationStep(nodeLabel(node, context), hasValidValue(arg.value), [`Function: ${node.name}`], [arg]);
            } catch (error: any) {
               return explanationStep(nodeLabel(node, context), false, [`Function: ${node.name}`, `Blocked error: ${error.message || error}`]);
            }
         }
         if (node.name === "firstValue" || node.name === "coalesce") {
            const steps: PLAExpressionExplanationStep[] = [];
            for (const arg of node.args) {
               try {
                  const step = explainNode(arg, context);
                  steps.push(step);
                  if (hasValidValue(step.value)) return explanationStep(nodeLabel(node, context), step.value, [`Function: ${node.name}`], steps);
               } catch (error: any) {
                  steps.push(explanationStep(nodeLabel(arg, context), undefined, [`Blocked error: ${error.message || error}`]));
               }
            }
            return explanationStep(nodeLabel(node, context), undefined, [`Function: ${node.name}`, "No argument returned a value"], steps);
         }
         const args = node.args.map((arg) => explainNode(arg, context));
         const result = explainAggregateValues(node.name, args.map((arg) => numeric(arg.value)), `${node.name}(...)`);
         return explanationStep(nodeLabel(node, context), result, [`Function: ${node.name}`], args);
      }
      case "traversal": {
         const attribute = explainTraversalAttributeReference(node.attribute, context);
         const traversal = explainTraversal(node.aggregator, node.relationType, attribute.attributeName, context, Boolean(node.deep), node.functionName || node.aggregator);
         return attribute.step ? explanationStep(nodeLabel(node, context), traversal.value, [`Resolved traversal attribute: ${attributeLabel(attribute.attributeName, context)}`], [attribute.step, traversal]) : traversal;
      }
      case "parentLookup": {
         const attribute = explainTraversalAttributeReference(node.attribute, context);
         const lookup = explainParentLookup(node.relationType, attribute.attributeName, context);
         return attribute.step ? explanationStep(nodeLabel(node, context), lookup.value, [`Resolved traversal attribute: ${attributeLabel(attribute.attributeName, context)}`], [attribute.step, lookup]) : lookup;
      }
      case "parentTraversal": {
         const attribute = explainTraversalAttributeReference(node.attribute, context);
         const traversal = explainParentTraversal(node.aggregator, node.parentRelationType, node.relationType, attribute.attributeName, context);
         return attribute.step ? explanationStep(nodeLabel(node, context), traversal.value, [`Resolved traversal attribute: ${attributeLabel(attribute.attributeName, context)}`], [attribute.step, traversal]) : traversal;
      }
      default: throw new Error("Unsupported expression node");
   }
}

function evaluateNode(node: ExpressionNode, context: PLAEvaluationContext): any {
   switch (node.type) {
      case "number": return node.value;
      case "string": return node.value;
      case "attribute": return resolveAttributeReference(node.name, context);
      case "binary": {
         const left = evaluateNode(node.left, context);
         const right = evaluateNode(node.right, context);
         if ([">", "<", ">=", "<=", "==", "!="].includes(node.operator)) return compare(node.operator, left, right);
         if (node.operator === "+") return numeric(left) + numeric(right);
         if (node.operator === "-") return numeric(left) - numeric(right);
         if (node.operator === "*") return numeric(left) * numeric(right);
         if (node.operator === "/") return numeric(left) / numeric(right);
         throw new Error(`Unsupported operator '${node.operator}'`);
      }
      case "conditional": return evaluateNode(node.condition, context) ? evaluateNode(node.whenTrue, context) : evaluateNode(node.whenFalse, context);
      case "function": {
         if (node.name === "hasValue") {
            if (node.args.length !== 1) throw new Error(`Cannot evaluate hasValue(...): expected exactly one argument but found ${node.args.length}`);
            try {
               return hasValidValue(evaluateNode(node.args[0], context));
            } catch {
               return false;
            }
         }
         if (node.name === "firstValue" || node.name === "coalesce") {
            for (const arg of node.args) {
               try {
                  const value = evaluateNode(arg, context);
                  if (hasValidValue(value)) return value;
               } catch {
                  // Keep searching for the first argument that can produce a usable value.
               }
            }
            return undefined;
         }
         return aggregateValues(node.name, node.args.map((arg) => numeric(evaluateNode(arg, context))), `${node.name}(...)`);
      }
      case "traversal": return evaluateTraversal(node.aggregator, node.relationType, resolveTraversalAttributeReference(node.attribute, context), context, Boolean(node.deep), node.functionName || node.aggregator);
      case "parentLookup": return evaluateParentLookup(node.relationType, resolveTraversalAttributeReference(node.attribute, context), context);
      case "parentTraversal": return evaluateParentTraversal(node.aggregator, node.parentRelationType, node.relationType, resolveTraversalAttributeReference(node.attribute, context), context);
      default: throw new Error("Unsupported expression node");
   }
}

export function evaluatePLAExpression(expression: string, context: PLAEvaluationContext): any {
   return evaluateNode(parsePLAExpression(expression), context);
}

export function explainPLAExpression(expression: string, context: PLAEvaluationContext): PLAExpressionExplanation {
   const parsed = parsePLAExpression(expression);
   const root = explainNode(parsed, context);
   return { expression: sanitizeExplanationText(nodeLabel(parsed, context), context), result: root.value, valueType: root.valueType, root, lines: formatExplanationLines(root, context) };
}

function aggregateValues(name: string, values: any[], label: string): any {
   if (name === "single") {
      if (values.length !== 1) throw new Error(`Cannot evaluate ${label}: expected exactly one related element but found ${values.length}`);
      return values[0];
   }
   if (name === "round") {
      if (values.length !== 1) throw new Error(`Cannot evaluate ${label}: expected exactly one argument but found ${values.length}`);
      return Math.round(numeric(values[0]));
   }
   if (values.length === 0) return 0;
   const numbers = values.map(numeric);
   switch (name) {
      case "sum": return numbers.reduce((acc, value) => acc + value, 0);
      case "avg": return numbers.reduce((acc, value) => acc + value, 0) / numbers.length;
      case "min": return Math.min(...numbers);
      case "max": return Math.max(...numbers);
      case "count": return numbers.length;
      default: throw new Error(`Unsupported function '${name}'`);
   }
}

function resolveTraversalAttributeReference(attribute: AttributeReference, context: PLAEvaluationContext): string {
   if (attribute.type === "literal") return attribute.name;
   if (attribute.type === "stringExpression") {
      const value = evaluateStringExpression(attribute.expression, context);
      if (!value.trim()) throw new Error(`Cannot evaluate [${stringExpressionLabel(attribute.expression, context)}] as traversal attribute name: expected a non-empty string`);
      return value.trim();
   }
   const value = resolveAttributeReference(attribute.name, context);
   if (typeof value !== "string" || !value.trim()) throw new Error(`Cannot evaluate [${attributeLabel(attribute.name, context)}] as traversal attribute name: expected a non-empty string`);
   return value.trim();
}

function stringExpressionValue(value: any, label: string): string {
   if (typeof value !== "string") throw new Error(`Cannot evaluate ${label} as string expression term: expected a string`);
   return value;
}

function evaluateStringExpression(node: StringExpressionNode, context: PLAEvaluationContext): string {
   switch (node.type) {
      case "constant": return node.value;
      case "attribute": return stringExpressionValue(resolveAttributeReference(node.name, context), `[${node.name}]`);
      case "concat": return evaluateStringExpression(node.left, context) + evaluateStringExpression(node.right, context);
      default: throw new Error("Unsupported string expression node");
   }
}

export function evaluateTraversal(aggregator: string, relationType: string, attributeName: string, context: PLAEvaluationContext, deep = false, functionName = aggregator): any {
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const relationLabel = relationTypeLabel(relationTypeId, context);
   const attributeLabelText = attributeLabel(attributeName, context);
   const targets = new Set<string>();
   const pending = [context.element.id];
   const visited = new Set<string>();
   while (pending.length > 0) {
      const currentId = pending.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      context.relations
         .filter((relation) => {
            if (relationTypeId === "outgoing") return relation.fromId === currentId;
            if (relationTypeId === "incoming") return relation.toId === currentId;
            return relation.fromId === currentId && relation.relationType === relationTypeId;
         })
         .forEach((relation) => {
            const targetId = relationTypeId === "incoming" ? relation.fromId : relation.toId;
            if (targetId === context.element.id || targets.has(targetId)) return;
            if (deep) {
               targets.add(targetId);
               pending.push(targetId);
               return;
            }
            const targetElement = context.elements.find((element) => element.id === targetId);
            if (!targetElement) return;
            const result = getElementAttributeValue(targetElement, attributeName, context);
            if (result.found) {
               targets.add(targetId);
            } else if (!visited.has(targetId)) {
               pending.push(targetId);
            }
         });
   }
   const values = Array.from(targets)
      .map((targetId) => getElementsById(context.elements).get(targetId))
      .filter(Boolean)
      .map((element) => {
         const targetElement = element as PLAElementRecord;
         return getElementAttributeValue(targetElement, attributeName, context).value;
      });
   return aggregateValues(aggregator, values, `${functionName}(${relationLabel}.${attributeLabelText})`);
}

function getElementAttributeValue(element: PLAElementRecord, attributeName: string, context: PLAEvaluationContext): { found: boolean; value?: any } {
   const attributeId = findAttributeIdByReference(attributeName, context, element);
   const definitions = context.project?.elementTypes[element.elementType]?.attributes || {};
   if (definitions[attributeId]?.type === "expression") {
      if (context.resolveCalculatedAttribute) return { found: true, value: context.resolveCalculatedAttribute(element, attributeId) };
      return { found: true, value: resolvePLACalculatedAttributes(element, context.project!, context.elements, context.relations)[attributeId] };
   }
   if (Object.prototype.hasOwnProperty.call(element.attributes, attributeId)) {
      return { found: true, value: resolveAttributeValue(element.attributes[attributeId], {}, attributeId) };
   }
   return { found: false };
}

export function evaluateParentLookup(relationType: string, attributeName: string, context: PLAEvaluationContext): any {
   if (!context.project) throw new Error("Cannot evaluate parent lookup without project definitions");
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const relationLabel = relationTypeLabel(relationTypeId, context);
   const attributeLabelText = attributeLabel(attributeName, context);
   const pending = [context.element.id];
   const visited = new Set<string>([context.element.id]);

   while (pending.length > 0) {
      const currentId = pending.shift()!;
      const parentRelations = getRelatedRelations(context, currentId, relationTypeId, "incoming");
      for (const relation of parentRelations) {
         if (visited.has(relation.fromId)) continue;
         visited.add(relation.fromId);
         const parentElement = getElementsById(context.elements).get(relation.fromId);
         if (!parentElement) throw new Error(`Cannot evaluate parent(${relationLabel}.${attributeLabelText}): missing parent element`);

         try {
            const result = getElementAttributeValue(parentElement, attributeName, context);
            if (result.found) return result.value;
         } catch (error: any) {
            throw new Error(`Cannot evaluate parent(${relationLabel}.${attributeLabelText}) on ${elementLabel(parentElement)}: ${error.message || error}`);
         }
         pending.push(parentElement.id);
      }
   }

   return 0;
}

export function evaluateParentTraversal(aggregator: string, parentRelationType: string, relationType: string, attributeName: string, context: PLAEvaluationContext): any {
   if (!context.project) throw new Error("Cannot evaluate parent traversal without project definitions");
   const parentRelationTypeId = findRelationTypeIdByReference(parentRelationType, context);
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const parentRelationLabel = relationTypeLabel(parentRelationTypeId, context);
   const relationLabel = relationTypeLabel(relationTypeId, context);
   const attributeLabelText = attributeLabel(attributeName, context);
   const pending = [context.element.id];
   const visited = new Set<string>([context.element.id]);

   while (pending.length > 0) {
      const currentId = pending.shift()!;
      const parentRelations = getRelatedRelations(context, currentId, parentRelationTypeId, "incoming");
      for (const parentRelation of parentRelations) {
         if (visited.has(parentRelation.fromId)) continue;
         visited.add(parentRelation.fromId);
         const parentElement = getElementsById(context.elements).get(parentRelation.fromId);
         if (!parentElement) throw new Error(`Cannot evaluate parent${aggregator}(${parentRelationLabel}.${relationLabel}.${attributeLabelText}): missing parent element`);

         const targetRelations = getRelatedRelations(context, parentElement.id, relationTypeId, "outgoing");
         if (targetRelations.length > 0) {
            const values = targetRelations.map((relation) => {
               const targetElement = getElementsById(context.elements).get(relation.toId);
               if (!targetElement) throw new Error(`Cannot evaluate parent${aggregator}(${parentRelationLabel}.${relationLabel}.${attributeLabelText}): missing related element`);
               return getElementAttributeValue(targetElement, attributeName, context).value;
            });
            return aggregateValues(aggregator, values, `parent${aggregator}(${parentRelationLabel}.${relationLabel}.${attributeLabelText})`);
         }

         pending.push(parentElement.id);
      }
   }

   return 0;
}

export function resolvePLACalculatedAttributes(element: PLAElementRecord, project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], options: { includeErrors?: boolean; errors?: { attributeId: string; message: string }[] } = {}): Record<string, any> {
   const definitions = project.elementTypes[element.elementType]?.attributes || {};
   const resolved: Record<string, any> = {};
   const resolving = new Set<string>();

   const resolve = (attributeId: string): any => {
      if (Object.prototype.hasOwnProperty.call(resolved, attributeId)) return resolved[attributeId];
      if (resolving.has(attributeId)) throw new Error(`Circular calculated attribute dependency: ${attributeId}`);
      const definition = definitions[attributeId];
      if (!definition || definition.type !== "expression") return resolveAttributeValue(element.attributes[attributeId], resolved, attributeId);
      resolving.add(attributeId);
      try {
         resolved[attributeId] = evaluatePLAExpression(definition.expression || "0", {
            element,
            elements,
            relations,
            project,
            calculated: resolved,
            resolveCalculatedAttribute: (targetElement, targetAttributeId) => {
               if (targetElement.id === element.id) return resolve(targetAttributeId);
               return resolvePLACalculatedAttributes(targetElement, project, elements, relations)[targetAttributeId];
            }
         });
      } catch (error: any) {
         options.errors?.push({ attributeId, message: error.message || String(error) });
         if (!options.includeErrors) throw error;
         resolved[attributeId] = error.message || String(error);
      } finally {
         resolving.delete(attributeId);
      }
      return resolved[attributeId];
   };

   Object.keys(definitions).forEach((attributeId) => {
      if (definitions[attributeId].type === "expression") resolve(attributeId);
   });
   return resolved;
}

export function resolvePLAElementExpressionAttributes(element: PLAElementRecord, project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], options: { includeErrors?: boolean } = {}): PLAElementRecord {
   const definitions = project.elementTypes[element.elementType]?.attributes || {};
   const calculated = resolvePLACalculatedAttributes(element, project, elements, relations, options);
   const attributes = { ...element.attributes };

   Object.entries(definitions).forEach(([attributeId, definition]) => {
      if (definition.type !== "expression") return;
      attributes[attributeId] = { mode: "constant", valueType: definition.valueType || "string", value: calculated[attributeId] };
   });

   return { ...element, attributes };
}

export function validatePLAData(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]): string[] {
   const errors: string[] = [];
   const ids = new Set<string>();
   elements.forEach((element) => {
      if (ids.has(element.id)) errors.push(`Duplicate element id: ${element.id}`);
      ids.add(element.id);
      if (!project.elementTypes[element.elementType]) errors.push(`Unknown element type: ${element.elementType}`);
      const definitions = project.elementTypes[element.elementType]?.attributes || {};
      Object.entries(definitions).forEach(([attributeId, definition]) => {
         if (definition.type === "expression") {
            const validation = validatePLAExpression(definition.expression || "", { project, elementTypeId: element.elementType });
            if (!validation.valid) errors.push(`${attributeId}: ${validation.error}`);
         }
         Object.entries(definition.alerts || {}).forEach(([alertId, alert]) => {
            const validation = validatePLAExpression(alert.booleanExpression, { project, elementTypeId: element.elementType });
            if (!validation.valid) errors.push(`${attributeId}.${alertId}: ${validation.error}`);
         });
      });
      try {
         resolvePLACalculatedAttributes(element, project, elements, relations);
      } catch (error: any) {
         errors.push(error.message || String(error));
      }
   });

   const relationIds = new Set<string>();
   relations.forEach((relation) => {
      if (relationIds.has(relation.id)) errors.push(`Duplicate relation id: ${relation.id}`);
      relationIds.add(relation.id);
      if (!project.relationTypes[relation.relationType]) errors.push(`Unknown relation type: ${relation.relationType}`);
      if (!ids.has(relation.fromId)) errors.push(`Missing from element: ${relation.fromId}`);
      if (!ids.has(relation.toId)) errors.push(`Missing to element: ${relation.toId}`);
   });

   const visiting = new Set<string>();
   const visited = new Set<string>();
   const outgoing = relations.reduce<Record<string, string[]>>((acc, relation) => {
      acc[relation.fromId] = [...(acc[relation.fromId] || []), relation.toId];
      return acc;
   }, {});
   const visit = (id: string): boolean => {
      if (visiting.has(id)) return true;
      if (visited.has(id)) return false;
      visiting.add(id);
      for (const target of outgoing[id] || []) {
         if (visit(target)) return true;
      }
      visiting.delete(id);
      visited.add(id);
      return false;
   };
   Array.from(ids).forEach((id) => {
      if (visit(id)) errors.push(`Circular element relation detected at ${id}`);
   });

   return errors;
}

export function getPLAAlerts(element: PLAElementRecord, project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]): string[] {
   return getPLAAlertDetails(element, project, elements, relations).map((alert) => alert.message);
}

export function getPLAAlertDetails(element: PLAElementRecord, project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]): { attributeId: string; message: string; optimizerMayOverride?: boolean }[] {
   const definitions = project.elementTypes[element.elementType]?.attributes || {};
   const alerts: { attributeId: string; message: string; optimizerMayOverride?: boolean }[] = [];
   const calculated = resolvePLACalculatedAttributes(element, project, elements, relations, { includeErrors: true, errors: alerts });
   Object.entries(definitions).forEach(([attributeId, definition]) => {
      Object.values(definition.alerts || {}).forEach((alert) => {
         try {
            if (evaluatePLAExpression(alert.booleanExpression, { element, elements, relations, project, calculated })) {
               alerts.push(alert.optimizerMayOverride ? { attributeId, message: alert.alertMessage, optimizerMayOverride: true } : { attributeId, message: alert.alertMessage });
            }
         } catch (error: any) {
            alerts.push({ attributeId, message: error.message || String(error) });
         }
      });
   });
   return alerts;
}
